import { addNotification } from '@/shared/states/notification';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createControlDescriptionWithAI,
  createFrameworkAPI,
  createTestingProceduresWithAI,
  deleteFrameworkAPI,
  fetchControlsDataAPI,
  getFrameworksListAPI,
  publishFrameworkAPI,
  refineTestingProceduresWithAI,
  saveFrameworkDraftAPI,
  submitFrameworkControlsAPI,
  updateFrameworkAPI,
  updateFrameworkControlsAPI,
  updateFrameworkResponseSchemaAPI,
  updateFrameworkSchemaAPI,
} from '../api';
import {
  CreateFrameworkResponse,
  FrameworkField,
  FrameworkPayload,
} from '../types';
import { useFrameworkWizardStore } from '../states';

const {setValidationErrors} = useFrameworkWizardStore.getState();
// Query Keys
export const FrameworkQueryKeys = {
  FrameworksList: 'frameworks-list',
  Framework: 'framework',
  FrameworkDraft: 'framework-draft',
} as const;

// Get Frameworks List Query
export const useGetFrameworksListQuery = () => {
  return useQuery({
    queryKey: [FrameworkQueryKeys.FrameworksList],
    queryFn: getFrameworksListAPI,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Create Framework Mutation (Start Framework Creation)
export const useCreateFrameworkMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: FrameworkPayload) => createFrameworkAPI(payload),
    onSuccess: (data: CreateFrameworkResponse) => {
      // Remove automatic download - template will only download when user clicks the download button

      queryClient.invalidateQueries({
        queryKey: [FrameworkQueryKeys.FrameworksList],
      });
    },
    onError: (error: any) => {
      // Extract error_message from backend response
      let backendErrorMessage = error?.response?.data?.error_message;
      const eT = error?.response?.data?.error_type;
      if (eT === 'Validation Error') {
        setValidationErrors({
          frameworkExist: backendErrorMessage || 'Framework name is required',
        });
        backendErrorMessage = `Please modify the name to continue. You may include a prefix or suffix (e.g., client name or assessment scope) to avoid duplication.`;
        addNotification({
          type: 'error',
          title: 'Framework Name Exist',
          message: backendErrorMessage || 'Failed to start framework creation',
        });
      } else {
        addNotification({
          type: 'error',
          message: backendErrorMessage || 'Failed to start framework creation',
        });
      }
    },
  });
};

// Submit Framework Template Mutation
export const useSaveFrameworkDraftMutation = () => {
  return useMutation({
    mutationFn: (payload: FrameworkPayload) => saveFrameworkDraftAPI(payload),
    onSuccess: (data: CreateFrameworkResponse) => {
      if (data && data.data && data.data.controlsUrl) {
        fetch(data.data.controlsUrl)
          .then((res) => res.json())
          .then((controls) => {
            if (Array.isArray(controls)) {
              addNotification({
                type: 'success',
                title: `${controls.length} ${controls.length > 1 ? 'Controls' : 'Control'} Imported Successfully`,
                message:
                  'You can edit the control description and testing procedures in the next step.',
              });
            } else {
              addNotification({
                type: 'success',
                title: 'Controls Imported Successfully',
                message:
                  'You can edit the control description and testing procedures in the next step.',
              });
            }
          })
          .catch(() => {
            addNotification({
              type: 'success',
              title: 'Controls Imported Successfully',
              message:
                'You can edit the control description and testing procedures in the next step.',
            });
          });
      }
    },
    onError: (error: any) => {
      const backendErrorMessage = error?.response?.data?.error_message;

      addNotification({
        type: 'error',
        message:
          backendErrorMessage ||
          error?.response?.data?.message ||
          'Failed to submit framework template',
      });
    },
  });
};

// Update Framework Mutation
export const useUpdateFrameworkMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: Partial<FrameworkPayload>;
    }) => updateFrameworkAPI(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FrameworkQueryKeys.FrameworksList],
      });
    },
    onError: (error: any) => {
      const backendErrorMessage = error?.response?.data?.error_message;

      addNotification({
        type: 'error',
        message:
          backendErrorMessage ||
          error?.response?.data?.message ||
          'Failed to update framework',
      });
    },
  });
};

// Delete Framework Mutation
export const useDeleteFrameworkMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteFrameworkAPI(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FrameworkQueryKeys.FrameworksList],
      });
    },
    onError: (error: any) => {
      // Remove notification here; handled in main component
      addNotification({
        type: 'error',
        message: error?.response?.data?.message || 'Failed to delete framework',
      });
    },
  });
};

// Publish Framework Mutation
export const usePublishFrameworkMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: FrameworkPayload) => {
      return publishFrameworkAPI(payload);
    },
    onSuccess: (data: CreateFrameworkResponse) => {
      // Remove duplicate notification - it's handled in the main component
      queryClient.invalidateQueries({
        queryKey: [FrameworkQueryKeys.FrameworksList],
      });
    },
    onError: (error: any) => {
      console.error('Publish mutation error:', error);
    },
  });
};

// Fetch Controls Data Query
export const useFetchControlsDataQuery = (
  url: string | undefined,
  enabled: boolean = false
) => {
  return useQuery({
    queryKey: [FrameworkQueryKeys.Framework, 'controls', url],
    queryFn: () => fetchControlsDataAPI(url!),
    enabled: enabled && !!url,
    staleTime: 0, // Always fetch fresh data
    refetchOnWindowFocus: false,
  });
};

// Fetch Response Schema Data Query
export const useFetchResponseSchemaQuery = (
  url: string | undefined,
  enabled: boolean = false
) => {
  return useQuery({
    queryKey: [FrameworkQueryKeys.Framework, 'response-schema', url],
    queryFn: () => fetchControlsDataAPI(url!), // Reuse the same fetch function since it's just JSON
    enabled: enabled && !!url,
    staleTime: 0, // Always fetch fresh data
    refetchOnWindowFocus: false,
  });
};

// AI Testing Procedures Hooks
export const useCreateTestingProceduresWithAI = () => {
  return useMutation({
    mutationFn: ({
      frameworkId,
      frameworkName,
      controlDescription,
    }: {
      frameworkId: string;
      frameworkName: string;
      controlDescription: string;
    }) =>
      createTestingProceduresWithAI(
        frameworkId,
        frameworkName,
        controlDescription
      ),
    onSuccess: () => { },
    onError: (error: any) => {
      console.error('Failed to create testing procedures with AI:', error);
      addNotification({
        type: 'error',
        message:
          error?.message || 'Failed to create testing procedures with AI',
      });
    },
  });
};

export const useRefineTestingProceduresWithAI = () => {
  return useMutation({
    mutationFn: ({
      frameworkId,
      frameworkName,
      controlDescription,
      testingProcedures,
    }: {
      frameworkId: string;
      frameworkName: string;
      controlDescription: string;
      testingProcedures: string;
    }) =>
      refineTestingProceduresWithAI(
        frameworkId,
        frameworkName,
        controlDescription,
        testingProcedures
      ),
    onSuccess: () => { },
    onError: (error: any) => {
      console.error('Failed to refine testing procedures with AI:', error);
      addNotification({
        type: 'error',
        message:
          error?.message || 'Failed to refine testing procedures with AI',
      });
    },
  });
};

// Bulk create testing procedures for multiple controls
export const useBulkCreateTestingProceduresWithAI = () => {
  return useMutation({
    mutationFn: async ({
      frameworkId,
      frameworkName,
      controls,
    }: {
      frameworkId: string;
      frameworkName: string;
      controls: Array<{ id: string; description: string }>;
    }) => {
      const results = [];

      // Process controls sequentially to avoid API timeouts
      for (const control of controls) {
        try {
          const testingProcedures = await createTestingProceduresWithAI(
            frameworkId,
            frameworkName,
            control.description
          );
          results.push({
            controlId: control.id,
            testingProcedures,
            success: true,
          });
        } catch (error) {
          console.error(
            `Failed to create testing procedures for control ${control.id}:`,
            error
          );
          results.push({
            controlId: control.id,
            error: error,
            success: false,
          });
        }
      }

      return results;
    },
    onSuccess: (results) => {
      const successCount = results.filter((r) => r.success).length;
      const totalCount = results.length;
    },
    onError: (error: any) => {
      console.error('Failed to bulk create testing procedures:', error);
      addNotification({
        type: 'error',
        message: 'Failed to create testing procedures for controls',
      });
    },
  });
};

// Update Framework Controls Hook (Step 3)
export const useUpdateFrameworkControlsMutation = () => {
  return useMutation({
    mutationFn: ({
      frameworkStepId,
      frameworkId,
      controlsUrl,
    }: {
      frameworkStepId: string;
      frameworkId: string;
      controlsUrl: string;
    }) => updateFrameworkControlsAPI(frameworkStepId, frameworkId, controlsUrl),
    onSuccess: () => { },
    onError: (error: any) => {
      console.error('Failed to update framework controls:', error);
      addNotification({
        type: 'error',
        message: error?.message || 'Failed to update framework controls',
      });
    },
  });
};

// Submit Framework Controls Mutation (Step 3)
export const useSubmitFrameworkControlsMutation = () => {
  return useMutation({
    mutationFn: ({
      frameworkId,
      frameworkName,
      controlsUrl,
    }: {
      frameworkId: string;
      frameworkName: string;
      controlsUrl: string; // Back to URL
    }) => submitFrameworkControlsAPI(frameworkId, frameworkName, controlsUrl),
    onSuccess: () => { },
    onError: (error: any) => {
      console.error('Failed to submit framework controls:', error);
      addNotification({
        type: 'error',
        message: error?.message || 'Failed to submit framework controls',
      });
    },
  });
};

// Update Framework Schema Mutation (Step 1 when framework exists)
export const useUpdateFrameworkSchemaMutation = () => {
  return useMutation({
    mutationFn: ({
      frameworkStepId,
      frameworkId,
      fields,
    }: {
      frameworkStepId: string;
      frameworkId: string;
      fields: FrameworkField[];
    }) => updateFrameworkSchemaAPI(frameworkStepId, frameworkId, fields),
    onSuccess: () => { },
    onError: (error: any) => {
      addNotification({
        type: 'error',
        message: error?.message || 'Failed to update framework schema',
      });
    },
  });
};

// Update Framework Response Schema Mutation (Step 4)
export const useUpdateFrameworkResponseSchemaMutation = () => {
  return useMutation({
    mutationFn: ({
      frameworkStepId,
      frameworkId,
      responseSchemaUrl,
    }: {
      frameworkStepId: string;
      frameworkId: string;
      responseSchemaUrl: string;
    }) =>
      updateFrameworkResponseSchemaAPI(
        frameworkStepId,
        frameworkId,
        responseSchemaUrl
      ),
    onSuccess: () => { },
    onError: (error: any) => {
      addNotification({
        type: 'error',
        message: error?.message || 'Failed to update response schema',
      });
    },
  });
};

export { useStepFourAutoSave } from './useStepFourAutoSave';
export { useStepThreeAutoSave } from './useStepThreeAutoSave';

// New hook for creating control description with AI
export const useCreateControlDescriptionWithAI = () => {
  return useMutation({
    mutationFn: ({
      frameworkId,
      frameworkName,
      controlDescription,
    }: {
      frameworkId: string;
      frameworkName: string;
      controlDescription: string;
    }) =>
      createControlDescriptionWithAI(
        frameworkId,
        frameworkName,
        controlDescription
      ),
    onSuccess: () => { },
    onError: (error: any) => {
      console.error('Failed to create control description with AI:', error);
      addNotification({
        type: 'error',
        message:
          error?.message || 'Failed to create control description with AI',
      });
    },
  });
};

// New hook for refining testing procedures with AI (returns both fields)
export const useRefineTestingProceduresWithAIv2 = () => {
  return useMutation({
    mutationFn: ({
      frameworkId,
      frameworkName,
      controlDescription,
      testingProcedures,
    }: {
      frameworkId: string;
      frameworkName: string;
      controlDescription: string;
      testingProcedures: string;
    }) =>
      refineTestingProceduresWithAI(
        frameworkId,
        frameworkName,
        controlDescription,
        testingProcedures
      ),
    onSuccess: () => { },
    onError: (error: any) => {
      console.error('Failed to refine testing procedures with AI:', error);
      addNotification({
        type: 'error',
        message:
          error?.message || 'Failed to refine testing procedures with AI',
      });
    },
  });
};
